import type { Address } from '@wagmi/core';
import { fetchBalance } from '@wagmi/core';
import type { useNetwork } from 'wagmi';

import {
  CHAIN_ID_TO_KEY_MAP,
  CHAIN_ID_TO_TYPE_MAP,
  getCurrentChainId,
  WEB3_CONFIG,
} from '../../../lib';
import { INITIAL_WALLET_DETAILS } from './provider';
import type { WalletDetails } from './types';

export const getInitialChain = () => {
  const currentChainId = getCurrentChainId();

  return WEB3_CONFIG.chains[CHAIN_ID_TO_KEY_MAP[currentChainId]];
};

const getTokenContracts = (chainId: number) => {
  const { stars, usdt, usdc } =
    WEB3_CONFIG.contracts[CHAIN_ID_TO_KEY_MAP[chainId]];

  return { stars, usdt, usdc };
};

const formatBalance = (balance: Awaited<ReturnType<typeof fetchBalance>>) => ({
  raw: balance.value,
  formatted: balance.formatted,
  decimals: balance.decimals,
});

export const chainToWalletChain = (
  chain: ReturnType<typeof useNetwork>['chain'],
) => {
  if (!chain) {
    return INITIAL_WALLET_DETAILS.chain;
  }

  return {
    id: chain.id,
    key: CHAIN_ID_TO_KEY_MAP[chain.id],
    type: CHAIN_ID_TO_TYPE_MAP[chain.id],
    explorerUrl: chain.blockExplorers?.default?.url,
    isSupported: !chain.unsupported,
  };
};

export const getWalletBalance = async (
  address: Address,
  chainId: typeof WEB3_CONFIG.chains[keyof typeof WEB3_CONFIG.chains]['id'],
): Promise<WalletDetails['balance']> => {
  const tokens = getTokenContracts(chainId);

  const balances = await Promise.allSettled([
    fetchBalance({ address }),
    fetchBalance({ address, token: tokens.usdt }),
    fetchBalance({ address, token: tokens.usdc }),
    fetchBalance({ address, token: tokens.stars }),
  ]);

  const [native, usdt, usdc, stars] = balances.map(b =>
    b.status === 'rejected'
      ? INITIAL_WALLET_DETAILS.balance.native
      : formatBalance(b.value),
  );

  return {
    native,
    usdt,
    usdc,
    stars,
  };
};
